'use client';

import React, { useEffect, useRef, useState } from 'react';
import Logo from '~/components/elements/common/Logo';
import SearchHeader from '~/components/shared/headers/modules/SearchHeader';
import DesktopNavigation from '~/components/shared/navigation/DesktopNavigation';
import HeaderActions from '~/components/shared/headers/modules/HeaderActions';
import MenuCategoriesDropdown from '~/components/shared/menus/MenuCategoriesDropdown';
import TopBar from '~/components/shared/headers/TopBar';


const HeaderDefault = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isStickyVisible, setIsStickyVisible] = useState(false);

  const headerRef = useRef(null);
  const stickyRef = useRef(null);
  const stickyVisibleRef = useRef(false);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setIsMounted(true);
    }, 100);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop || 0;

      const shouldShowStickyHeader = scrollTop >= 300;

      if (shouldShowStickyHeader && !stickyVisibleRef.current) {
        stickyVisibleRef.current = true;

        if (animationFrameRef.current) {
          window.cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = window.requestAnimationFrame(() => {
          setIsStickyVisible(true);
          animationFrameRef.current = null;
        });

        return;
      }

      if (!shouldShowStickyHeader && stickyVisibleRef.current) {
        stickyVisibleRef.current = false;

        if (animationFrameRef.current) {
          window.cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        setIsStickyVisible(false);
      }
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);

      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <>
      <style jsx global>{`
        :root {
          --logo-to-search-gap: 35px;
          --header-content-height: 80px;
          --search-height: 44px;
          --search-bg: #f4f5fa;
          --search-border: #c9ccd8;
          --search-placeholder: #6b7280;
        }
        #headerSticky {
          position: relative !important;
          z-index: 2000 !important;
          background: #ffffff !important;
          // border-bottom: 2px solid #000000 !important;
        }
        #headerSticky .header__top,
        #headerStickyOverlay .header__top {
          min-height: var(--header-content-height) !important;
        }
        #headerSticky .header__top > .ps-container,
        #headerStickyOverlay .header__top > .ps-container {
          min-height: var(--header-content-height) !important;
          display: flex !important;
          align-items: center !important;
        }
        #headerSticky .header__left,
        #headerStickyOverlay .header__left {
          display: flex !important;
          align-items: center !important;
        }
        #headerSticky .category-logo-group,
        #headerStickyOverlay .category-logo-group {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          flex: 0 0 auto !important;
          gap: 18px !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #headerSticky .category-wrapper,
        #headerStickyOverlay .category-wrapper,
        #headerSticky .logo-wrapper,
        #headerStickyOverlay .logo-wrapper {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          flex: 0 0 auto !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #headerSticky .header__center,
        #headerStickyOverlay .header__center {
          display: flex !important;
          align-items: center !important;
          min-width: 0 !important;
          height: var(--header-content-height) !important;
          min-height: var(--header-content-height) !important;
          margin: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          padding-left: var(--logo-to-search-gap) !important;
          padding-right: 0 !important;
          box-sizing: border-box !important;
          position: relative !important;
          overflow: visible !important;
        }
        #headerSticky .header__center > *,
        #headerStickyOverlay .header__center > * {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
          height: var(--search-height) !important;
          min-height: var(--search-height) !important;
          max-height: var(--search-height) !important;
          margin: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          box-sizing: border-box !important;
          position: relative !important;
          overflow: visible !important;
        }
        #headerSticky .header__center .ps-search--mobile,
        #headerStickyOverlay .header__center .ps-search--mobile,
        #headerSticky .header__center .ps-form--search-mobile,
        #headerStickyOverlay .header__center .ps-form--search-mobile,
        #headerSticky .header__center .form-group--nest,
        #headerStickyOverlay .header__center .form-group--nest {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
          height: var(--search-height) !important;
          min-height: var(--search-height) !important;
          max-height: var(--search-height) !important;
          margin: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          box-sizing: border-box !important;
          position: relative !important;
          overflow: visible !important;
        }
        #headerSticky .header__center input,
        #headerStickyOverlay .header__center input {
          min-width: 0 !important;
          max-width: 100% !important;
          height: 40px !important;
          min-height: 40px !important;
          max-height: 40px !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          color: #111111 !important;
          background: #ffffff !important;
        }
        #headerSticky .header__center .form-group--nest button,
        #headerStickyOverlay .header__center .form-group--nest button {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          height: 40px !important;
          min-height: 40px !important;
          max-height: 40px !important;
          margin: 0 !important;
          box-sizing: border-box !important;
        }
        #headerSticky .header__center .search-results-dropdown button,
        #headerStickyOverlay .header__center .search-results-dropdown button {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
        }
        #headerSticky .header__center .pt-12,
        #headerStickyOverlay .header__center .pt-12 {
          padding-top: 0 !important;
        }
        #headerSticky .header__center .ps-search--mobile > *:not(form),
        #headerStickyOverlay .header__center .ps-search--mobile > *:not(form),
        #headerSticky .header__center .form-group--nest > *:not(input):not(button),
        #headerStickyOverlay .header__center .form-group--nest > *:not(input):not(button) {
          position: absolute !important;
          top: 100% !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          height: auto !important;
          max-height: none !important;
          margin-top: 4px !important;
          z-index: 3000 !important;
        }
        #headerSticky .category-wrapper .menu--product-categories .menu__toggle,
        #headerStickyOverlay
          .category-wrapper
          .menu--product-categories
          .menu__toggle {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: auto !important;
          min-width: auto !important;
          height: 34px !important;
          min-height: 34px !important;
          margin: 0 !important;
          padding: 0 12px !important;
          gap: 7px !important;
          color: #111111 !important;
          background: #ffffff !important;
          border: 1px solid #111111 !important;
          border-radius: 8px !important;
          font-size: 13px !important;
          font-weight: 700 !important;
          line-height: 1 !important;
          white-space: nowrap !important;
          cursor: pointer !important;
          box-sizing: border-box !important;
          transition:
            background 0.2s ease,
            box-shadow 0.2s ease !important;
        }
        #headerSticky
          .category-wrapper
          .menu--product-categories
          .menu__toggle:hover,
        #headerStickyOverlay
          .category-wrapper
          .menu--product-categories
          .menu__toggle:hover {
          color: #111111 !important;
          background: #f5f5f5 !important;
          border-color: #111111 !important;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12) !important;
        }
        #headerSticky
          .category-wrapper
          .menu--product-categories
          .menu__toggle
          span,
        #headerStickyOverlay
          .category-wrapper
          .menu--product-categories
          .menu__toggle
          span {
          color: #111111 !important;
          font-size: 13px !important;
          font-weight: 700 !important;
          line-height: 1 !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        #headerSticky
          .category-wrapper
          .menu--product-categories
          .menu__toggle
          i,
        #headerStickyOverlay
          .category-wrapper
          .menu--product-categories
          .menu__toggle
          i {
          color: #111111 !important;
          font-size: 15px !important;
          line-height: 1 !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        #headerStickyOverlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          z-index: 2100 !important;
          transform: translate3d(0, -110%, 0) !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
          color: #111111 !important;
          background: rgba(255, 255, 255, 0.98) !important;
          backdrop-filter: blur(16px) !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
          transition: none !important;
        }
        #headerStickyOverlay.transitions-enabled {
          transition:
            transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94),
            opacity 0.3s ease,
            visibility 0s linear 0.45s !important;
        }
        #headerStickyOverlay.animate-in {
          transform: translate3d(0, 0, 0) !important;
          opacity: 1 !important;
          visibility: visible !important;
          pointer-events: auto !important;
          color: #111111 !important;
          background: rgba(255, 255, 255, 0.98) !important;
          transition:
            transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94),
            opacity 0.3s ease,
            visibility 0s linear 0s !important;
        }
        #headerStickyOverlay .header__top,
        #headerStickyOverlay .ps-container,
        #headerStickyOverlay .header__left,
        #headerStickyOverlay .header__center,
        #headerStickyOverlay .header__right {
          color: #111111 !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        #headerStickyOverlay .header__top {
          position: relative !important;
          background: transparent !important;
        }
        #headerStickyOverlay .header__top::after {
          content: '' !important;
          position: absolute !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          height: 1px !important;
          background: #111111 !important;
          pointer-events: none !important;
          z-index: 20 !important;
        }
        #headerStickyOverlay .logo-wrapper .ps-logo,
        #headerStickyOverlay .logo-wrapper .ps-logo img {
          opacity: 1 !important;
          visibility: visible !important;
        }
        #headerStickyOverlay .logo-wrapper .ps-logo {
          display: flex !important;
          align-items: center !important;
        }
        #headerStickyOverlay .logo-wrapper .ps-logo img {
          display: block !important;
          width: auto !important;
          height: auto !important;
          max-width: 175px !important;
          max-height: 58px !important;
          object-fit: contain !important;
          filter: none !important;
          mix-blend-mode: normal !important;
        }
        #headerStickyOverlay .logo-wrapper img[src='/static/img/logo_light.png'] {
          display: block !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        #headerStickyOverlay .header__right,
        #headerStickyOverlay .header__right a,
        #headerStickyOverlay .header__right span,
        #headerStickyOverlay .header__actions,
        #headerStickyOverlay .header__extra,
        #headerStickyOverlay .header__extra_text,
        #headerStickyOverlay .header__extra_text span,
        #headerStickyOverlay .ps-block--user-header,
        #headerStickyOverlay .ps-block--user-header a {
          opacity: 1 !important;
          visibility: visible !important;
        }
        #headerStickyOverlay .count-badge,
        #headerStickyOverlay .count-badge i,
        #headerStickyOverlay .cart-count,
        #headerStickyOverlay .cart-count i {
          color: #ffffff !important;
          background: #d90000 !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        #headerStickyOverlay .ps-block__footer a {
          color: #ffffff !important;
        }
        html,
        body,
        .__variable_6a73e9 {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
        #headerStickyOverlay .ps-cart__footer .ps-btn.ps-btn--disabled,
        #headerStickyOverlay .ps-cart__footer a.ps-btn[aria-disabled="true"] {
          opacity: 0.45 !important;
          pointer-events: none !important;
          cursor: not-allowed !important;
        }

        /* ===== Suchleiste ===== */
        #headerSticky .header__center .form-group--nest,
        #headerStickyOverlay .header__center .form-group--nest {
          height: var(--search-height) !important;
          padding: 0 4px 0 18px !important;
          background: var(--search-bg) !important;
          border: 1px solid var(--search-border) !important;
          border-radius: 999px !important;
          box-shadow: none !important;
          transition:
            border-color 0.2s ease,
            background 0.2s ease,
            box-shadow 0.2s ease !important;
        }
        #headerSticky .header__center .form-group--nest:focus-within,
        #headerStickyOverlay .header__center .form-group--nest:focus-within {
          background: #ffffff !important;
          border-color: #111111 !important;
          box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.08) !important;
        }
        #headerSticky .header__center .form-group--nest input,
        #headerStickyOverlay .header__center .form-group--nest input {
          flex: 1 1 auto !important;
          height: 100% !important;
          min-height: 0 !important;
          max-height: none !important;
          padding: 0 !important;
          font-size: 14px !important;
          color: #111111 !important;
          background: transparent !important;
          border: 0 !important;
          border-radius: 0 !important;
          outline: none !important;
          box-shadow: none !important;
        }
        #headerSticky .header__center .form-group--nest input::placeholder,
        #headerStickyOverlay .header__center .form-group--nest input::placeholder {
          color: var(--search-placeholder) !important;
          opacity: 1 !important;
        }
        #headerSticky .header__center .form-group--nest > button,
        #headerStickyOverlay .header__center .form-group--nest > button {
          flex: 0 0 auto !important;
          width: 58px !important;
          height: 36px !important;
          min-height: 36px !important;
          max-height: 36px !important;
          margin: 0 !important;
          padding: 0 !important;
          color: #ffffff !important;
          background: #2f3134 !important;
          border: 0 !important;
          border-radius: 999px !important;
          cursor: pointer !important;
          transition:
            background 0.2s ease,
            transform 0.1s ease !important;
        }
        #headerSticky .header__center .form-group--nest > button:hover,
        #headerStickyOverlay .header__center .form-group--nest > button:hover {
          background: #454951 !important;
        }
        #headerSticky .header__center .form-group--nest > button:active,
        #headerStickyOverlay .header__center .form-group--nest > button:active {
          transform: scale(0.96);
        }
        #headerSticky .header__center .form-group--nest > button i,
        #headerSticky .header__center .form-group--nest > button svg,
        #headerStickyOverlay .header__center .form-group--nest > button i,
        #headerStickyOverlay .header__center .form-group--nest > button svg {
          color: #ffffff !important;
          fill: currentColor;
          font-size: 18px !important;
          width: 18px;
          height: 18px;
        }

        @media (max-width: 768px) {
          :root {
            --logo-to-search-gap: 14px;
            --header-content-height: 72px;
          }
          #headerSticky
            .category-wrapper
            .menu--product-categories
            .menu__toggle,
          #headerStickyOverlay
            .category-wrapper
            .menu--product-categories
            .menu__toggle {
            height: 32px !important;
            min-height: 32px !important;
            padding: 0 10px !important;
            gap: 6px !important;
          }
          #headerStickyOverlay .logo-wrapper .ps-logo img {
            max-width: 125px !important;
            max-height: 48px !important;
          }
          #headerSticky .header__center .form-group--nest,
          #headerStickyOverlay .header__center .form-group--nest {
            padding-left: 14px !important;
          }
          #headerSticky .header__center .form-group--nest > button,
          #headerStickyOverlay .header__center .form-group--nest > button {
            width: 48px !important;
          }
        }
        @media (max-width: 480px) {
          :root {
            --logo-to-search-gap: 8px;
            --header-content-height: 64px;
          }
          #headerSticky
            .category-wrapper
            .menu--product-categories
            .menu__toggle
            span,
          #headerStickyOverlay
            .category-wrapper
            .menu--product-categories
            .menu__toggle
            span {
            display: none !important;
          }
          #headerSticky
            .category-wrapper
            .menu--product-categories
            .menu__toggle,
          #headerStickyOverlay
            .category-wrapper
            .menu--product-categories
            .menu__toggle {
            width: 34px !important;
            min-width: 34px !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <header
        ref={headerRef}
        className="header header--1"
        data-sticky="true"
        id="headerSticky"
      >
        <TopBar />

        <div className="header__top">
          <div className={`ps-container ${isSearchOpen ? 'search-open' : ''}`}>
            <div className="header__left">
              <div className="category-logo-group">
                <div className="category-wrapper">
                  <MenuCategoriesDropdown />
                </div>

                <div className="logo-wrapper">
                  <Logo />
                </div>
              </div>
            </div>

            <div
              className={`header__center ${
                isSearchOpen ? 'search-expanded pt-12' : ''
              }`}
            >
              <SearchHeader onSearchToggle={setIsSearchOpen} />
            </div>

            <div
              className={`header__right ${
                isSearchOpen ? 'search-open' : ''
              }`}
            >
              <HeaderActions />
            </div>
          </div>
        </div>

        <DesktopNavigation />
      </header>

      <header
        ref={stickyRef}
        className={[
          'header',
          'header--1',
          'header--sticky',
          isMounted ? 'transitions-enabled' : '',
          isStickyVisible ? 'animate-in' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-sticky="true"
        id="headerStickyOverlay"
      >
        <div className="header__top">
          <div className={`ps-container ${isSearchOpen ? 'search-open' : ''}`}>
            <div className="header__left">
              <div className="category-logo-group">
                <div className="category-wrapper">
                  <MenuCategoriesDropdown />
                </div>

                <div className="logo-wrapper">
                  <Logo />
                </div>
              </div>
            </div>

            <div
              className={`header__center ${
                isSearchOpen ? 'search-expanded pt-12' : ''
              }`}
            >
              <SearchHeader onSearchToggle={setIsSearchOpen} />
            </div>

            <div
              className={`header__right ${
                isSearchOpen ? 'search-open' : ''
              }`}
            >
              <HeaderActions />
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default HeaderDefault;